import type { CriticalSignal } from "@shared/labs/level-assessment";
import type { SignalStrength } from "@shared/labs/level-assessment-axis-signals";
import { ExternalLink, Quote } from "lucide-react";
import { useState } from "react";
import { C, DM, UI } from "./ui-tokens";

const STRENGTH_TEXT: Record<SignalStrength, string> = {
  strong: "Forte",
  moderate: "Moderado",
  weak: "Fraco",
};
const STRENGTH_FILLED: Record<SignalStrength, number> = { strong: 3, moderate: 2, weak: 1 };

/** Strength as a 3-bar meter + label (forte=3 · moderado=2 · fraco=1). Shared
 *  by the decisive cards and the "all signals" table. */
export function StrengthBars({ strength }: { strength: SignalStrength }) {
  const filled = STRENGTH_FILLED[strength];
  const accent = strength === "strong" ? C.ink : C.ink50;
  const bar = (on: boolean) => (
    <span style={{ width: 4, height: 11, borderRadius: 1, background: on ? accent : C.ink20 }} />
  );
  return (
    <span
      style={{
        ...UI,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        fontWeight: 600,
        color: strength === "strong" ? C.ink : C.ink50,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ display: "inline-flex", gap: 2 }}>
        {bar(filled >= 1)}
        {bar(filled >= 2)}
        {bar(filled >= 3)}
      </span>
      {STRENGTH_TEXT[strength]}
    </span>
  );
}

function DecisiveCard({ sig, linkedinUrl }: { sig: CriticalSignal; linkedinUrl?: string | null }) {
  const [open, setOpen] = useState(false);
  const evidence = sig.evidence ?? [];
  const hasEvidence = evidence.length > 0;
  const valueColor = sig.kind === "yes" ? C.greenFg : sig.kind === "no" ? C.ink50 : C.ink;

  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 11,
        padding: "14px 15px",
        background: C.white,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <span style={{ ...DM, fontSize: 13.5, fontWeight: 700, color: C.ink, lineHeight: 1.3 }}>
          {sig.title}
        </span>
        <span
          style={{
            ...UI,
            fontSize: 13,
            fontWeight: 700,
            color: valueColor,
            whiteSpace: "nowrap",
            flex: "none",
          }}
        >
          {sig.valueLabel}
        </span>
      </div>

      {sig.rubricConnection && (
        <div style={{ ...UI, fontSize: 12.5, color: C.ink70, lineHeight: 1.5, marginTop: 8 }}>
          {sig.rubricConnection}
        </div>
      )}

      <div
        style={{
          marginTop: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <StrengthBars strength={sig.strength} />
        {hasEvidence && (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            data-testid={`decisive-cite-${sig.key}`}
            style={{
              ...UI,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              fontWeight: 600,
              color: C.red,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <Quote size={13} /> {open ? "Ocultar evidência" : "Ver no perfil"}
          </button>
        )}
      </div>

      {open && hasEvidence && (
        <div
          style={{
            marginTop: 11,
            borderTop: `1px dashed ${C.border}`,
            paddingTop: 11,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {evidence.map((e, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: evidence list is static per render and has no stable id.
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {e.role && (
                <div style={{ ...DM, fontSize: 12, fontWeight: 700, color: C.ink }}>{e.role}</div>
              )}
              {e.meta && <div style={{ ...UI, fontSize: 11, color: C.ink50 }}>{e.meta}</div>}
              {e.quote && (
                <div
                  style={{
                    ...UI,
                    fontSize: 12.5,
                    color: C.ink70,
                    lineHeight: 1.5,
                    paddingLeft: 11,
                    borderLeft: `2px solid ${C.red}`,
                    marginTop: 2,
                  }}
                >
                  {e.quote}
                </div>
              )}
            </div>
          ))}
          {linkedinUrl && (
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                ...UI,
                alignSelf: "flex-start",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11.5,
                fontWeight: 600,
                color: C.ink50,
                textDecoration: "none",
              }}
            >
              <ExternalLink size={12} /> Abrir no LinkedIn
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The decisive signals for one axis: the routed signals the explainability
 * layer flagged as critical to the inferred level. Each card states the
 * humanized signal + value, why it ties to the rubric, and reveals — inline —
 * the profile citation (role · dates · quote) with a link to open LinkedIn.
 */
export function DecisiveSignals({
  signals,
  linkedinUrl,
}: {
  signals: CriticalSignal[];
  linkedinUrl?: string | null;
}) {
  if (signals.length === 0) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      {signals.map((sig) => (
        <DecisiveCard key={sig.key} sig={sig} linkedinUrl={linkedinUrl} />
      ))}
    </div>
  );
}
